import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { ProductSliderSkeletonComponent } from "@/components/Product/ProductSlider/components/skeleton/productSliderSkeleton.component";
import { ProductSliderSourceContextProvider } from "@/components/Product/ProductSlider/context/productSliderSource.context";
import { filterVisible } from "@/components/Product/ProductSlider/productSlider.helpers";

type SkuProductSliderProps = {
    skus: Array<string>;
    serverSideProducts?: Array<Product<ProductFieldSet.ProductTile>>;
};

export const SkuProductSlider = ({
    skus,
    children,
    serverSideProducts
}: RequireChildrenFor<SkuProductSliderProps>) => {
    const [products, setProducts] = useState(serverSideProducts);

    const [{ loading, error }, loadProducts] = useBFFProductServiceApi<
        any,
        any,
        Array<Product<ProductFieldSet.ProductTile>>
    >(
        {
            url: `/products`,
            method: "GET"
        },
        {
            manual: true,
            useCache: false,
            autoCancel: false
        }
    );

    useEffect(() => {
        if (isEmpty(skus)) return;
        if (isDefined(products)) return;
        if (loading || error) return;

        loadProducts({
            params: {
                sku: skus.join(","),
                fields: ProductFieldSet.ProductTile
            }
        })
            .then(({ data }) => data)
            .then(filterVisible)
            .then(setProducts);
    }, [products, skus, loading, loadProducts, error]);

    if (error) {
        return null;
    } else if (isDefined(products) && isEmpty(products)) {
        return null;
    } else if (isDefined(products) && !isEmpty(products)) {
        return (
            <ProductSliderSourceContextProvider products={products}>
                {children}
            </ProductSliderSourceContextProvider>
        );
    } else {
        return <ProductSliderSkeletonComponent />;
    }
};
